import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Grid, makeStyles } from '@material-ui/core';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import * as Popover from '../components/PopoverMenu';

import notification from '../../../config/notification';
import lineEndpoint from '../../../api/services/lineEndpoint';

import { apostilleListColumns } from '../table-configs/columns-schemas';
import { fetchLine } from 'app/store/slices/line';
import { activationPermissionaryValidationSchema } from '../form-configs/validation-schemas';
import { errorHandler } from '../../../utils/errorHandler';
import { URLS } from 'app/config/urls';

import InfoLineCompany from '../components/InfoLineCompany';
import ToggleActiveModal from './modals/ToggleActiveModal';
import Loading from '../../../layouts/Loading';
import ListsFilters from '../components/ListsFilters';
import SaveBox from '../components/SaveBox';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';

const TODAY = moment(new Date()).format();

const ApostillesList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [apostilles, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadApostilles } = actions;

    const cdLinhaFromPath = React.useRef(match.params.cdLinha);
    const selectedLine = useSelector((state) => state.line.selected);
    const activeUser = useSelector((state) => state.auth.registrationInfo);
    const isStrip = useSelector((state) => state.line.isStrip);

    const [loading, setLoading] = useState(false);
    const [loadingRows, setLoadingRows] = useState(true);
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('linhaApostila.dtEmissao');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [canAddNewRevision, setCanAddNewRevision] = useState(false);

    const [openActivateModal, setOpenActivateModal] = useState(false);

    const initialFilters = useMemo(() => {
        const result = {
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
        if (cdLinhaFromPath && cdLinhaFromPath.current) {
            result.cdLinha = Number(cdLinhaFromPath.current);
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdLinhaFromPath]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'apostilles-filters');

    const [rows, setRows] = useState([]);
    const open = Boolean(editEl);

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `linhaApostila.${property}`;
        if (property === 'nuSolicitacaoFmt') {
            temp['campoOrdenacao'] = `linhaApostila.cdSolicitacao`;
        } else if (property === 'nuApostilaFmt') {
            temp['campoOrdenacao'] = `linhaApostila.nuApostila`;
        } else if (property === 'boInativada') {
            temp['campoOrdenacao'] = `linhaApostila.boAtivo`;
        }
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePage(null, 0);
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            if (cdLinhaFromPath && cdLinhaFromPath.current) {
                await dispatch(fetchLine(cdLinhaFromPath.current));
            }
            setLoading(false);
        })();
    }, [dispatch]);

    useEffect(() => {
        (async () => {
            setLoadingRows(true);
            await fetch();
            setLoadingRows(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apostilles.page, apostilles.pageSize, filters]);

    useEffect(() => {
        (() => {
            if (selectedLine) {
                setCanAddNewRevision(true);
            }
        })();
    }, [selectedLine]);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            const temp = { ...row };
            temp.dtEmissao = moment(temp.dtEmissao).format('DD/MM/YYYY');
            temp.dtInicioValidade = moment(temp.dtInicioValidade).format('DD/MM/YYYY');
            temp.nuSolicitacaoFmt = temp.solicitacao?.nuSolicitacaoFmt;
            temp.boInativada = !temp.boAtivo;
            return temp;
        });
    };

    useEffect(() => {
        (() => {
            setRows(prepareRows(apostilles.content));
        })();
    }, [apostilles]);

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    function handleChangePage(e, page) {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    }

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleNewApostilleButton = async () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE_APOSTILLE_NEW, params));
    };

    async function fetch() {
        const params = {
            page: filters.page ? filters.page : apostilles.page,
            size: filters.pageSize ? filters.pageSize : apostilles.pageSize,
            request: filters
        };
        const response = await lineEndpoint.getAposttiles(params);
        if (response && response.status === 200) {
            if (response.status === 200) {
                let data = response.data;
                if (data.content.length === 0) {
                    resetTable();
                } else {
                    loadApostilles(data);
                }
            }
        }
    }

    const handleClickToggleActivation = async () => {
        const boAtivo = selectedEntry?.boAtivo;
        if (!boAtivo) {
            await handleSubmitToggleActivation(selectedEntry);
        } else {
            setOpenActivateModal(true);
        }
    };

    const handleSubmitToggleActivation = async (values) => {
        try {
            setLoading(true);
            const isActivating = values.boAtivo === false;
            const payload = {
                boAtivar: !values.boAtivo,
                cdLinhaApostila: values.cdLinhaApostila,
                cdUsuarioResponsavel: activeUser.cdUsuario,
                deMotivo: values?.deMotivo || '',
                deObservacao: values?.deObservacao || '',
                dtCadastro: values?.dtCadastro || TODAY,
            };

            const response = await lineEndpoint.inativateApostille(payload);

            if (response.status === 200) {
                await fetch();
                notification(`Apostila ${isActivating ? 'ativada' : 'inativada'} com sucesso`, {
                    toastId: 'APOSTILLE_ACTIVATION',
                    type: 'success',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        setLoading(false);
        setOpenActivateModal(false);
        handleCloseEditMenu();
    };

    const handleGoBack = () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE, params));
    };

    return (
        <>
            {loading && <Loading />}
            <PageWrapper
                title="Lista e Histórico de Apostilas"
                actionButton={
                    canAddNewRevision ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            className={classes.addButton}
                            onClick={handleNewApostilleButton}>
                            Nova
                        </Button>
                    ) : (
                        <></>
                    )
                }>
                <Grid container spacing={4}>
                    <Grid item container spacing={3} direction={'row'}>
                        <InfoLineCompany selectedLine={selectedLine} />
                    </Grid>

                    <ListsFilters filters={filters} setFilters={setFilters} hasSituation={false} />

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={apostilleListColumns}
                            rows={rows}
                            loading={loadingRows}
                            onMore={handleOpenEditMenu}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: apostilles.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : apostilles.pageSize,
                                page: filters.page ? filters.page : apostilles.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <ToggleActiveModal
                isCurrentActive={selectedEntry?.boAtivo}
                open={openActivateModal}
                onClose={() => setOpenActivateModal(false)}
                data={selectedEntry}
                onSubmit={handleSubmitToggleActivation}
                apostille={true}
                formValidationSchema={activationPermissionaryValidationSchema}
                formInitialValues={{
                    cdLinha: selectedEntry?.cdLinha,
                    deMotivo: '',
                    deObservacao: '',
                    dtCadastro: TODAY,
                }}
            />
            <Popover.ApostilleList
                onEdit={handleOpenEditMenu}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/apostilles`}
                dataId={selectedEntry?.cdLinhaApostila}
                data={selectedEntry}
                onClickActivate={handleClickToggleActivation}
                isActive={selectedEntry?.boAtivo}
                isStrip={isStrip}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default ApostillesList;
